import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2, Target, Brain, Wallet, Workflow, Users, Calendar, Clock, PhoneCall, Mail, ChevronRight, MessageCircle, Flame, BarChart3, TrendingUp, Sparkles, Shield } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
const sectionFade = {
  hidden: {
    opacity: 0,
    y: 12
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5
    }
  }
};
const services = [{
  icon: <Brain className="h-6 w-6" aria-hidden data-id="element-5" />,
  title: 'AI Consulting',
  desc: 'Strategic guidance on leveraging artificial intelligence to enhance both personal growth journeys and business team operations.'
}, {
  icon: <Workflow className="h-6 w-6" aria-hidden data-id="element-6" />,
  title: 'Custom AI Development',
  desc: 'Custom-built AI systems designed specifically for your unique goals, learning patterns, and lifestyle preferences—whether for individual use or team implementation.'
}, {
  icon: <TrendingUp className="h-6 w-6" aria-hidden data-id="element-7" />,
  title: 'AI Implementation Training',
  desc: 'Personalized training to help both individuals and teams maximize the potential of your custom AI systems across all aspects of life and business.'
}];
const valuePoints = ['Ultra-personalized approach', 'Absolute discretion', 'Seamless integration'];
const steps = [{
  title: 'Private Consultation',
  body: 'Share your vision and objectives in a confidential discovery session with our founder.',
  icon: <MessageCircle className="h-5 w-5" data-id="element-8" />
}, {
  title: 'Strategic Design',
  body: 'We craft a tailored AI strategy customized to your specific requirements and aspirations.',
  icon: <Target className="h-5 w-5" data-id="element-9" />
}, {
  title: 'Custom Development',
  body: 'Our team builds your personalized AI systems with meticulous attention to detail.',
  icon: <Brain className="h-5 w-5" data-id="element-10" />
}, {
  title: 'Elite Support',
  body: 'Ongoing premium support and refinement to ensure your AI systems evolve with your needs.',
  icon: <Users className="h-5 w-5" data-id="element-11" />
}];
export function FireLitSite() {
  return <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-925 to-slate-900 text-slate-100" data-id="element-12">
      {/* Nav */}
      <header className="sticky top-0 z-30 backdrop-blur supports-[backdrop-filter]:bg-slate-950/50 border-b border-slate-800" data-id="element-13">
        <div className="mx-auto max-w-6xl px-4 py-4 flex items-center justify-between" data-id="element-14">
          <div className="flex items-center gap-3" data-id="element-15">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-amber-400/20 ring-1 ring-amber-300/50 shadow-lg shadow-amber-300/10" data-id="element-16">
              <Flame className="h-7 w-7 text-amber-300" data-id="element-17" />
            </div>
            <div className="flex flex-col" data-id="element-18">
              <span className="font-bold text-2xl tracking-tight text-amber-300" data-id="element-19">
                FireLit
              </span>
              <span className="text-xs text-slate-400" data-id="element-20">
                Personalized AI Excellence
              </span>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm text-slate-300" data-id="element-21">
            <a href="#services" className="hover:text-white" data-id="element-22">
              Services
            </a>
            <a href="#contact" className="hover:text-white font-medium text-amber-300" data-id="element-23">
              Schedule Consultation
            </a>
          </nav>
          <div className="flex items-center gap-2" data-id="element-24">
            <Button asChild className="rounded-2xl" data-id="element-25">
              <a href="#contact" className="inline-flex items-center gap-2" data-id="element-26">
                <PhoneCall className="h-4 w-4" data-id="element-27" /> Private Consultation
              </a>
            </Button>
          </div>
        </div>
      </header>
      {/* Floating CTA */}
      <div className="fixed bottom-6 right-6 z-50 md:hidden" data-id="element-28">
        <Button asChild size="lg" className="rounded-full shadow-lg h-14 w-14 p-0" data-id="element-29">
          <a href="tel:+17205792378" aria-label="Call us" data-id="element-30">
            <PhoneCall className="h-6 w-6" data-id="element-31" />
          </a>
        </Button>
      </div>
      {/* Hero */}
      <section className="relative overflow-hidden" data-id="element-32">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(60%_50%_at_50%_0%,rgba(251,191,36,0.12),transparent_60%)]" data-id="element-33" />
        <div className="mx-auto max-w-6xl px-4 py-24 md:py-32 grid md:grid-cols-2 gap-12 items-center" data-id="element-34">
          <motion.div initial="hidden" animate="show" variants={sectionFade} data-id="element-35">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-amber-300/30 bg-amber-300/10 text-amber-300 text-xs font-medium mb-8" data-id="element-36">
              <Clock className="h-3 w-3" data-id="element-37" /> Limited availability for new clients
            </div>
            <div className="mb-10" data-id="element-38">
              <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.2,
              duration: 0.7
            }} className="flex items-center gap-3 mb-3" data-id="element-39">
                <Flame className="h-12 w-12 text-amber-300" data-id="element-40" />
                <h1 className="text-6xl md:text-7xl font-bold tracking-tight text-amber-300" data-id="element-41">
                  FireLit
                </h1>
              </motion.div>
              <h2 className="text-3xl md:text-4xl font-semibold leading-tight mt-3" data-id="element-42">
                Custom-crafted <span className="text-white" data-id="element-43">AI Systems</span>{' '}
                for Extraordinary Growth
              </h2>
            </div>
            <p className="mt-4 text-slate-300 max-w-prose text-lg leading-relaxed" data-id="element-44">
              We craft ultra-personalized artificial intelligence solutions for
              both discerning individuals and visionary business leaders with
              teams. FireLit delivers custom AI systems that elevate your
              personal and professional growth to unprecedented heights through
              advanced knowledge retrieval, precision-tuned models, and
              sophisticated workflow automation.
            </p>
            <div className="mt-8 flex flex-wrap gap-4" data-id="element-45">
              <Button asChild size="lg" className="rounded-2xl" data-id="element-46">
                <a href="#contact" className="inline-flex items-center gap-2" data-id="element-47">
                  <Calendar className="h-5 w-5" data-id="element-48" /> Reserve Your Consultation
                </a>
              </Button>
              <a href="#services" className="inline-flex items-center gap-2 text-slate-200 hover:text-white" data-id="element-49">
                Explore our services <ArrowRight className="h-4 w-4" data-id="element-50" />
              </a>
            </div>
            <div className="mt-8 flex flex-wrap gap-4 text-sm text-slate-300" data-id="element-51">
              {valuePoints.map(v => <span key={v} className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-4 py-2" data-id="element-52">
                  <CheckCircle2 className="h-4 w-4 text-amber-300" data-id="element-53" /> {v}
                </span>)}
            </div>
          </motion.div>
          <motion.div initial={{
          opacity: 0,
          scale: 0.98
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.5
        }} data-id="element-54">
            <div className="relative" data-id="element-55">
              <div className="absolute -inset-2 rounded-3xl bg-amber-300/10 blur-xl" data-id="element-56" />
              <Card className="relative rounded-3xl border-slate-800 bg-slate-900/60 backdrop-blur" data-id="element-57">
                <CardHeader data-id="element-58">
                  <CardTitle className="flex items-center gap-2" data-id="element-59">
                    <Sparkles className="h-5 w-5 text-amber-300" data-id="element-60" />
                    Personalized AI Systems
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid gap-5" data-id="element-61">
                  <div className="flex items-start gap-3" data-id="element-62">
                    <div className="mt-1 text-amber-300" data-id="element-63">
                      <Brain className="h-6 w-6" data-id="element-64" />
                    </div>
                    <div data-id="element-65">
                      <div className="font-medium" data-id="element-66">
                        Tailored to Your Unique Goals
                      </div>
                      <p className="text-sm text-slate-300" data-id="element-67">
                        AI systems meticulously crafted to align with your
                        specific vision, preferences, and aspirations using
                        proprietary context-aware technologies and specialized
                        model architectures—for both individual users and
                        business teams.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-68">
                    <div className="mt-1 text-amber-300" data-id="element-69">
                      <Target className="h-6 w-6" data-id="element-70" />
                    </div>
                    <div data-id="element-71">
                      <div className="font-medium" data-id="element-72">
                        Accelerated Personal Growth
                      </div>
                      <p className="text-sm text-slate-300" data-id="element-73">
                        Harness the power of AI to enhance your capabilities and
                        achieve your full potential faster, whether as an
                        individual or as a business team.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-74">
                    <div className="mt-1 text-amber-300" data-id="element-75">
                      <Workflow className="h-6 w-6" data-id="element-76" />
                    </div>
                    <div data-id="element-77">
                      <div className="font-medium" data-id="element-78">Seamless Integration</div>
                      <p className="text-sm text-slate-300" data-id="element-79">
                        Systems designed to enhance existing workflows for both
                        individuals and teams without disruption or complexity.
                      </p>
                    </div>
                  </div>
                  <div className="mt-2 pt-4 border-t border-slate-800" data-id="element-80">
                    <Button asChild className="w-full rounded-2xl" data-id="element-81">
                      <a href="#contact" className="inline-flex items-center justify-center gap-2" data-id="element-82">
                        Schedule Your Private Consultation{' '}
                        <ChevronRight className="h-4 w-4" data-id="element-83" />
                      </a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </motion.div>
        </div>
      </section>
      {/* About */}
      <section id="about" className="mx-auto max-w-6xl px-4 py-16 md:py-20" data-id="element-84">
        <motion.div initial="hidden" whileInView="show" viewport={{
        once: true
      }} variants={sectionFade} data-id="element-85">
          <div className="flex items-center gap-2 mb-6" data-id="element-86">
            <Flame className="h-7 w-7 text-amber-300" data-id="element-87" />
            <h2 className="text-3xl md:text-4xl font-semibold" data-id="element-88">
              The <span className="text-amber-300" data-id="element-89">FireLit</span> Philosophy
            </h2>
          </div>
          <p className="mt-4 text-slate-300 max-w-3xl text-lg leading-relaxed" data-id="element-90">
            FireLit represents the convergence of elite AI expertise and a
            profound understanding of the unique challenges faced by
            high-achieving individuals and entrepreneurs. We believe that truly
            transformative growth requires AI systems as unique as you
            are—meticulously crafted to align with your specific goals,
            preferences, and vision through intelligent knowledge integration,
            optimized language instruction, and seamless workflow automation.
          </p>
          <p className="mt-4 text-slate-300 max-w-3xl text-lg leading-relaxed" data-id="element-91">
            Our team of AI specialists, strategists, and implementation experts
            work exclusively with a limited clientele, ensuring the highest
            level of personalization and discretion in every engagement.
          </p>
          <div className="mt-6 inline-flex items-center gap-2 text-amber-300 font-medium" data-id="element-92">
            <a href="#contact" className="inline-flex items-center gap-2 hover:underline text-lg" data-id="element-93">
              Discuss your vision with our founder{' '}
              <ArrowRight className="h-4 w-4" data-id="element-94" />
            </a>
          </div>
        </motion.div>
      </section>
      {/* Services */}
      <section id="services" className="mx-auto max-w-6xl px-4 py-16 md:py-20" data-id="element-95">
        <div className="flex items-center gap-2 mb-6" data-id="element-96">
          <Flame className="h-7 w-7 text-amber-300" data-id="element-97" />
          <h2 className="text-3xl md:text-4xl font-semibold" data-id="element-98">
            <span className="text-amber-300" data-id="element-99">FireLit</span> Services
          </h2>
        </div>
        <p className="mt-3 text-slate-300 max-w-3xl text-lg" data-id="element-100">
          Our comprehensive approach encompasses strategic consulting, custom AI
          development, and personalized implementation training.
        </p>
        <div className="mt-10 grid gap-8 md:grid-cols-3" data-id="element-101">
          {services.map(s => <Card key={s.title} className="rounded-3xl border-slate-800 bg-slate-900/60" data-id="element-102">
              <CardHeader data-id="element-103">
                <CardTitle className="flex items-center gap-2" data-id="element-104">
                  <span className="text-amber-300" data-id="element-105">{s.icon}</span>
                  {s.title}
                </CardTitle>
              </CardHeader>
              <CardContent data-id="element-106">
                <p className="text-slate-300" data-id="element-107">{s.desc}</p>
                {s.title === 'AI Consulting' && <ul className="mt-4 text-sm text-slate-300 list-disc pl-5 space-y-2" data-id="element-108">
                    <li data-id="element-109">
                      Strategic AI roadmapping for personal growth and business
                      team development
                    </li>
                    <li data-id="element-110">AI opportunity assessment and prioritization</li>
                    <li data-id="element-111">Privacy and security architecture planning</li>
                    <li data-id="element-112">Competitive intelligence and AI landscape analysis</li>
                  </ul>}
                {s.title === 'Custom AI Development' && <ul className="mt-4 text-sm text-slate-300 list-disc pl-5 space-y-2" data-id="element-113">
                    <li data-id="element-114">
                      Personalized AI assistants with context-aware knowledge
                      retrieval
                    </li>
                    <li data-id="element-115">Custom data analytics and insight generation</li>
                    <li data-id="element-116">
                      Automated workflow systems for business and personal life
                    </li>
                    <li data-id="element-117">Precision-tuned models for strategic planning</li>
                  </ul>}
                {s.title === 'AI Implementation Training' && <ul className="mt-4 text-sm text-slate-300 list-disc pl-5 space-y-2" data-id="element-118">
                    <li data-id="element-119">
                      One-on-one training sessions for individuals and
                      comprehensive team training
                    </li>
                    <li data-id="element-120">
                      Integration guidance for existing systems and workflows
                    </li>
                    <li data-id="element-121">Advanced usage techniques and optimization</li>
                    <li data-id="element-122">Ongoing support and capability expansion</li>
                  </ul>}
                <div className="mt-6 pt-3" data-id="element-123">
                  <Button asChild variant="outline" className="w-full rounded-xl border-amber-300/20 text-amber-300 hover:bg-amber-300/10" data-id="element-124">
                    <a href="#contact" className="inline-flex items-center justify-center gap-1 text-sm" data-id="element-125">
                      Learn More <ArrowRight className="h-3 w-3" data-id="element-126" />
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>)}
        </div>
      </section>
      {/* Contact / CTA */}
      <section id="contact" className="mx-auto max-w-6xl px-4 py-16 md:py-20" data-id="element-127">
        <div className="rounded-3xl border border-slate-800 bg-gradient-to-r from-slate-900 to-slate-875 p-8 md:p-12 relative overflow-hidden" data-id="element-128">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(70%_70%_at_50%_100%,rgba(251,191,36,0.15),transparent_70%)]" data-id="element-129" />
          <div className="relative" data-id="element-130">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-amber-300/30 bg-amber-300/10 text-amber-300 text-xs font-medium mb-6" data-id="element-131">
              <Clock className="h-3 w-3" data-id="element-132" /> Limited availability for new clients
            </div>
            <div className="flex items-center gap-3 mb-6" data-id="element-133">
              <Calendar className="h-8 w-8 text-amber-300" data-id="element-134" />
              <h3 className="text-3xl md:text-4xl font-semibold" data-id="element-135">
                Limited availability for new client engagements
              </h3>
            </div>
            <p className="mt-3 text-slate-300 max-w-prose text-lg leading-relaxed" data-id="element-136">
              To maintain our exceptional level of personalization and service,
              we accept only a limited number of new clients each quarter.
              Secure your private consultation with our founder today.
            </p>
            <div className="mt-10 grid gap-6 md:grid-cols-2 max-w-3xl" data-id="element-137">
              <div className="rounded-2xl border border-slate-700 p-6 bg-slate-800/50" data-id="element-138">
                <div className="flex items-center gap-3" data-id="element-139">
                  <PhoneCall className="h-6 w-6 text-amber-300" data-id="element-140" />
                  <div data-id="element-141">
                    <h4 className="font-medium text-lg" data-id="element-142">Direct Line</h4>
                    <p className="text-sm text-slate-300" data-id="element-143">
                      Speak confidentially with our founder
                    </p>
                  </div>
                </div>
                <Button asChild size="lg" className="mt-5 w-full rounded-xl" data-id="element-144">
                  <a href="tel:+17205792378" className="text-lg" data-id="element-145">
                    720-579-2378
                  </a>
                </Button>
              </div>
              <div className="rounded-2xl border border-slate-700 p-6 bg-slate-800/50" data-id="element-146">
                <div className="flex items-center gap-3" data-id="element-147">
                  <Mail className="h-6 w-6 text-amber-300" data-id="element-148" />
                  <div data-id="element-149">
                    <h4 className="font-medium text-lg" data-id="element-150">Private Email</h4>
                    <p className="text-sm text-slate-300" data-id="element-151">
                      We respond within 12 hours
                    </p>
                  </div>
                </div>
                <Button asChild size="lg" variant="outline" className="mt-5 w-full rounded-xl border-amber-300/20 text-amber-300 hover:bg-amber-300/10" data-id="element-152">
                  <a href="mailto:jason@firelit.io?subject=Private%20Consultation%20Request" data-id="element-153">
                    jason@firelit.io
                  </a>
                </Button>
              </div>
            </div>
            <p className="mt-8 text-sm text-slate-400 flex items-center gap-2" data-id="element-154">
              <CheckCircle2 className="h-4 w-4 text-amber-300" data-id="element-155" />
              All consultations are protected by our confidentiality agreement
            </p>
          </div>
        </div>
      </section>
      {/* Footer */}
      <footer className="border-t border-slate-800" data-id="element-156">
        <div className="mx-auto max-w-6xl px-4 py-10 text-sm text-slate-400" data-id="element-157">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4" data-id="element-158">
            <div className="flex items-center gap-3" data-id="element-159">
              <Flame className="h-6 w-6 text-amber-300" data-id="element-160" />
              <div data-id="element-161">
                <div className="font-semibold text-xl text-amber-300" data-id="element-162">
                  FireLit
                </div>
                <div data-id="element-163">Personalized AI Excellence • Centennial, CO</div>
              </div>
            </div>
            <div className="flex flex-col md:flex-row items-start md:items-center gap-6" data-id="element-164">
              <a className="hover:text-slate-200 inline-flex items-center gap-2" href="mailto:jason@firelit.io" data-id="element-165">
                <Mail className="h-4 w-4 text-amber-300" data-id="element-166" /> jason@firelit.io
              </a>
              <a className="hover:text-slate-200 inline-flex items-center gap-2" href="tel:+17205792378" data-id="element-167">
                <PhoneCall className="h-4 w-4 text-amber-300" data-id="element-168" /> 720-579-2378
              </a>
            </div>
          </div>
          <div className="mt-6" data-id="element-169">
            © {new Date().getFullYear()}{' '}
            <span className="text-amber-300 font-medium" data-id="element-170">FireLit</span>. All
            rights reserved.
          </div>
        </div>
      </footer>
    </div>;
}